import { ref } from 'vue'
import { aiApi } from '@/modules/ai/api'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { sanitizeRichText } from '@/core/utils/sanitizer'
import { marked } from 'marked'
import { logger } from '@/core/utils/logger'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  htmlContent?: string
  timestamp: Date
}

const isOpen = ref(false)
const messages = ref<ChatMessage[]>([])
const inputText = ref('')
const isLoading = ref(false)
const error = ref<string | null>(null)

export function useChatAssistant() {
  const projectsStore = useProjectsStore()

  function toggle() {
    isOpen.value = !isOpen.value
  }

  function open() {
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  async function sendMessage() {
    const text = inputText.value.trim()
    if (!text || isLoading.value) return

    const currentProject = projectsStore.currentProject
    if (!currentProject) {
      error.value = 'Není vybrán žádný projekt'
      return
    }

    error.value = null
    inputText.value = ''

    // Add user message
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date()
    }
    messages.value.push(userMessage)

    isLoading.value = true
    try {
      // Build prompt with conversation history for context
      const historyParts = messages.value.map(m =>
        m.role === 'user' ? `User: ${m.content}` : `Assistant: ${m.content}`
      )
      const fullPrompt = historyParts.length > 1
        ? `${historyParts.slice(0, -1).join('\n')}\n\nUser: ${text}`
        : text

      const response = await aiApi.sendPrompt({ prompt: fullPrompt }, currentProject.code)
      const rawHtml = await marked.parse(response.content || '')
      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: response.content || '',
        htmlContent: sanitizeRichText(rawHtml),
        timestamp: new Date()
      }
      messages.value.push(assistantMessage)
    } catch (err) {
      logger.error('AI chat error:', err)
      error.value = 'Chyba při komunikaci s AI'
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Omlouvám se, došlo k chybě při zpracování vašeho dotazu.',
        timestamp: new Date()
      }
      messages.value.push(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  function clearHistory() {
    messages.value = []
    error.value = null
  }

  return {
    isOpen,
    messages,
    inputText,
    isLoading,
    error,
    toggle,
    open,
    close,
    sendMessage,
    clearHistory
  }
}
